import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import {
    AuthScreen,
    AuthBody,
    AuthFooter,
    ScreenHeader,
    TextField,
    PrimaryButton,
} from "@/screens/auth/_ui";
import { useAuth } from "@/context/AuthContext";
import { getOTP, clearOTP } from "@/lib/storage";

const ForgotReset = () => {
    const navigate = useNavigate();
    const { resetPassword } = useAuth();
    const [pw, setPw] = useState("");
    const [confirmPw, setConfirmPw] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [errors, setErrors] = useState({});
    const [success, setSuccess] = useState(false);

    // The identifier is carried by the OTP record; if it's missing we bail.
    const otpRecord = getOTP();
    // Note: OTP is cleared on successful verify, so we fallback to route-state? Simpler:
    // we keep the identifier in memory by reading it BEFORE user reaches here.
    // For robustness: we also accept a globally stored neara.resetIdentifier.
    const identifier =
        otpRecord?.identifier ||
        (() => {
            try {
                return JSON.parse(localStorage.getItem("neara.resetIdentifier") || "null");
            } catch {
                return null;
            }
        })();

    const validate = () => {
        const e = {};
        if (!pw) e.pw = "Create a new password";
        else if (pw.length < 6) e.pw = "At least 6 characters";
        if (pw !== confirmPw) e.confirmPw = "Passwords don't match";
        return e;
    };

    const handleSubmit = () => {
        const e = validate();
        if (Object.keys(e).length) {
            setErrors(e);
            return;
        }
        if (!identifier) {
            setErrors({ form: "Session expired. Please start again." });
            return;
        }
        const res = resetPassword(identifier, pw);
        if (!res.ok) {
            setErrors({ form: res.error });
            return;
        }
        clearOTP();
        localStorage.removeItem("neara.resetIdentifier");
        setSuccess(true);
        setTimeout(() => navigate("/auth/login", { replace: true }), 900);
    };

    return (
        <AuthScreen testId="forgot-reset-screen">
            <ScreenHeader title="New password" />
            <AuthBody>
                <div className="pt-4 space-y-1 animate-fade-up">
                    <h2 className="font-display text-[22px] font-semibold tracking-tight">
                        Set a new password
                    </h2>
                    <p className="text-[13.5px] text-muted-foreground">
                        Make sure it's something you'll remember.
                    </p>
                </div>

                <div className="mt-8 space-y-4 animate-fade-up" style={{ animationDelay: "80ms" }}>
                    <TextField
                        testId="forgot-new-password"
                        label="New password"
                        type={showPw ? "text" : "password"}
                        placeholder="At least 6 characters"
                        value={pw}
                        onChange={(v) => {
                            setPw(v);
                            if (errors.pw) setErrors({ ...errors, pw: null });
                        }}
                        error={errors.pw}
                        right={
                            <button
                                type="button"
                                data-testid="forgot-toggle-password"
                                onClick={() => setShowPw((s) => !s)}
                                className="h-9 w-9 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground"
                            >
                                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        }
                    />
                    <TextField
                        testId="forgot-confirm-password"
                        label="Confirm password"
                        type={showPw ? "text" : "password"}
                        placeholder="Re-enter password"
                        value={confirmPw}
                        onChange={(v) => {
                            setConfirmPw(v);
                            if (errors.confirmPw) setErrors({ ...errors, confirmPw: null });
                        }}
                        error={errors.confirmPw}
                    />

                    {errors.form && (
                        <div
                            data-testid="forgot-reset-error"
                            className="rounded-xl border border-destructive/40 bg-destructive/10 text-destructive text-[13px] font-medium px-4 py-3"
                        >
                            {errors.form}
                        </div>
                    )}

                    {success && (
                        <div
                            data-testid="forgot-reset-success"
                            className="rounded-xl border border-primary/40 bg-primary/10 text-primary text-[13px] font-medium px-4 py-3"
                        >
                            Password updated. Redirecting to login…
                        </div>
                    )}
                </div>
            </AuthBody>

            <AuthFooter>
                <PrimaryButton testId="forgot-reset-submit" onClick={handleSubmit}>
                    Update password
                </PrimaryButton>
            </AuthFooter>
        </AuthScreen>
    );
};

export default ForgotReset;
